import {
  EuiAccordion,
  EuiBasicTable,
  EuiFlexGroup,
  EuiFormRow,
  EuiIcon,
  EuiSpacer,
  EuiTableComputedColumnType,
  EuiText,
  EuiToken,
} from "@inscopix/ideas-eui";
import { ToolPathParam } from "components/ToolParamsGrid/ToolParamsGrid.types";
import {
  DataTableColumnDefinition,
  DataTableStoreState,
} from "pages/gdt/store/DataTableProvider.types";
import { useDataTableContext } from "pages/gdt/store/DataTableProvider";
import { FILE_TYPES_BY_KEY } from "types/FileTypes";
import { convertParamTypeToColumnKind } from "./FieldParamTable";
import { COLUMN_KIND_MAP } from "../ModalCreateColumn/ModalCreateColumn";
import { FieldColumnCompressed } from "./FieldColumnCompressed";
import { FieldTableCompressed } from "./FieldTableCompressed";
import { useCallback, useEffect, useMemo } from "react";
import { chain, isEqual, keyBy } from "lodash";
import { captureException } from "@sentry/react";
import { isDefined } from "utils/isDefined";
import { ToolSpec } from "types/json-schemas/type_guards";

const isFileColumnCompatibleWithParam = (
  colDef: Extract<DataTableColumnDefinition, { kind: "file" }>,
  param: ToolPathParam,
) => {
  const columnFileTypes = colDef.file_types.filter(
    (type) => type !== FILE_TYPES_BY_KEY.unknown.id,
  );
  const paramFileTypes = (param.type.file_filters ?? [])
    .map((filter) => FILE_TYPES_BY_KEY[filter.file_type]?.id)
    .filter(isDefined);

  if (columnFileTypes.length > 0 && paramFileTypes.length > 0) {
    return columnFileTypes.some((t) => paramFileTypes.includes(t));
  }

  const columnFileFormats = colDef.file_formats;
  const paramFileFormats = (param.type.file_filters ?? []).map(
    (f) => f.file_format,
  );

  if (columnFileFormats.length > 0 && paramFileFormats.length > 0) {
    return columnFileFormats.some((f) => paramFileFormats.includes(f));
  }

  return true;
};

const isColumnCompatibleWithParam = (
  colDef: DataTableColumnDefinition,
  param: ToolSpec["params"][number],
): boolean => {
  switch (param.type.param_type) {
    case "ToolBooleanParam":
      return colDef.kind === "boolean";
    case "ToolCellStatusParam":
      return colDef.kind === "cell_status";
    case "ToolChoiceParam":
      return colDef.kind === "text" || colDef.kind === "metadatum";
    case "ToolCroppingFrameParam":
    case "ToolRoiFrameParam":
      return colDef.kind === "roi_frame";
    case "ToolFloatRangeParam":
    case "ToolIntRangeParam":
      return (
        colDef.kind === "float" ||
        colDef.kind === "integer" ||
        colDef.kind === "metadatum"
      );
    case "ToolPathParam":
      return (
        colDef.kind === "file" &&
        isFileColumnCompatibleWithParam(colDef, param as ToolPathParam)
      );
    case "ToolStringParam":
      return colDef.kind === "text" || colDef.kind === "metadatum";
  }
};

type Item = {
  tableIdOptions: string[];
  param: ToolSpec["params"][number];
  tableId: string | undefined;
  columnId: string | undefined;
  onChangeTableMapping: (tableId: string | undefined) => void;
  onChangeColumnMapping: (
    tableId: string | undefined,
    columnId: string | undefined,
  ) => void;
  readOnly: boolean;
  columnsById: Record<
    string,
    DataTableStoreState["tables"][number]["columns"][number]
  >;
};

type FieldParamMappingProps = {
  toolSpec: ToolSpec;
  inputsLabel?: string;
  parametersLabel?: string;
  readOnly?: boolean;
  hideTableSelector: boolean;
  hideEmptyParamsTable?: boolean;
  tableIdOptions: string[];
  isAlwaysExpanded?: boolean;
  value: {
    [paramKey: string]:
      | {
          tableId: string | undefined;
          columnId: string | undefined;
        }
      | undefined;
  };
  onChange: (paramMap: {
    [paramKey: string]:
      | {
          tableId: string | undefined;
          columnId: string | undefined;
        }
      | undefined;
  }) => void;
  disabled?: boolean;
};

export const FieldParamMapping = ({
  toolSpec,
  inputsLabel = "Analysis inputs",
  parametersLabel = "Analysis parameters",
  readOnly = false,
  hideTableSelector,
  tableIdOptions,
  hideEmptyParamsTable = false,
  isAlwaysExpanded = false,
  value,
  onChange,
  disabled = false,
}: FieldParamMappingProps) => {
  /**
   * Gets the source column of a join column.
   */
  const getJoinColumnSource = useDataTableContext((s) => {
    // Create lookup map for columns
    const columnsById = chain(s.tables)
      .flatMap((t) => t.columns)
      .keyBy((c) => c.id)
      .value();

    /**
     * Gets the source column for a join column. Join columns can reference
     * other join columns so we have to search recursively.
     * @param columnId
     * @param depth The current recursion depth.
     * @returns The source column used by the join column or `undefined` if the
     * maximum recursion depth is reached.
     */
    const getSource = (
      columnId: string,
      depth = 0,
    ): (typeof s.tables)[number]["columns"][number] | undefined => {
      if (depth >= 50) {
        captureException(
          "Maximum recursion depth reach when calculating join column source",
        );
        return;
      }

      if (columnId in columnsById) {
        const column = columnsById[columnId];
        return column.definition.kind === "join" &&
          column.definition.source_column
          ? getSource(column.definition.source_column?.id, depth + 1)
          : column;
      }
    };

    return (columnId: string) => getSource(columnId);
  });

  const renderArrowColumn = useCallback(() => {
    return <EuiIcon type={"doubleArrowRight"} />;
  }, []);
  const columns: EuiTableComputedColumnType<Item>[] = useMemo(
    () => [
      {
        name: "Name",
        width: "25%",
        render: ({ param }) => param.name,
      },
      {
        name: "Type",
        width: "15%",
        render: ({ param }) => {
          const kind = convertParamTypeToColumnKind(param.type.param_type);
          return (
            <EuiFlexGroup alignItems="center" gutterSize="s">
              <EuiToken iconType={COLUMN_KIND_MAP[kind].icon} />
              <span>{COLUMN_KIND_MAP[kind].name}</span>
            </EuiFlexGroup>
          );
        },
      },
      {
        name: "",
        width: "50px",
        render: renderArrowColumn,
        mobileOptions: {
          show: false,
        },
      },
      {
        name: "Source table",
        render: ({
          readOnly,
          tableId,
          tableIdOptions,
          onChangeTableMapping,
        }) => {
          return (
            <FieldTableCompressed
              noItemsMessage="No sources selected"
              readOnly={readOnly}
              isTableSelectable={(id) => tableIdOptions.includes(id)}
              value={tableId}
              onChange={onChangeTableMapping}
              disabled={disabled}
            />
          );
        },
      },
      {
        name: "Source column",
        render: ({
          readOnly,
          param,
          tableId,
          columnId,
          columnsById,
          onChangeColumnMapping,
        }) => {
          return (
            <FieldColumnCompressed
              readOnly={readOnly}
              noItemsMessage="No columns matching this parameter's type found in source table"
              isColumnSuggested={(colDef) => {
                if (colDef.kind === "join") {
                  const sourceColumnId = colDef.source_column?.id;
                  const sourceColumn =
                    sourceColumnId !== undefined
                      ? getJoinColumnSource(sourceColumnId)
                      : undefined;
                  return (
                    sourceColumn !== undefined &&
                    isColumnCompatibleWithParam(sourceColumn.definition, param)
                  );
                } else {
                  return isColumnCompatibleWithParam(colDef, param);
                }
              }}
              tableId={tableId}
              value={columnId}
              onChange={(columnId) => {
                onChangeColumnMapping(tableId, columnId);
              }}
              disabled={disabled}
            />
          );
        },
      },
    ],
    [disabled, getJoinColumnSource, renderArrowColumn],
  );

  const columnsById = useDataTableContext((s) => {
    const columns = s.tables.flatMap((table) => table.columns);
    return keyBy(columns, (column) => column.id);
  });

  // Remove stale mappings when table ID options change
  useEffect(() => {
    const newValue = { ...value };

    Object.entries(newValue).forEach(([paramKey, mapping]) => {
      if (
        mapping?.tableId !== undefined &&
        !tableIdOptions.includes(mapping.tableId)
      ) {
        newValue[paramKey] = undefined;
      }
    });

    if (!isEqual(value, newValue)) {
      onChange(newValue);
    }
  }, [onChange, tableIdOptions, value]);

  const items: Item[] = toolSpec.params
    .map((param) => {
      let tableId = value[param.key]?.tableId;
      if (tableId === undefined && tableIdOptions.length === 1) {
        tableId = tableIdOptions[0];
      }

      return {
        readOnly,
        tableIdOptions,
        columnsById,
        param,
        tableId,
        columnId: value[param.key]?.columnId,
        onChangeTableMapping: (tableId: string | undefined) => {
          const newMap = { ...value };
          newMap[param.key] = { tableId, columnId: undefined };
          onChange(newMap);
        },
        onChangeColumnMapping: (
          tableId: string | undefined,
          columnId: string | undefined,
        ) => {
          const newMap = { ...value };
          newMap[param.key] = { tableId, columnId };
          onChange(newMap);
        },
      };
    })
    // Only display mapped params when in read-only mode
    .filter(({ param }) => {
      if (readOnly) {
        return value[param.key] !== undefined;
      } else {
        return true;
      }
    });

  const inputItems = items.filter(
    (item) => item.param.type.param_type === "ToolPathParam",
  );

  const paramItems = items.filter(
    (item) => item.param.type.param_type !== "ToolPathParam",
  );

  return (
    <>
      <EuiFormRow fullWidth label={inputsLabel}>
        <EuiBasicTable
          items={inputItems}
          columns={
            hideTableSelector
              ? columns.filter((c) => c.name !== "Source")
              : columns
          }
        />
      </EuiFormRow>

      <EuiSpacer size="l" />

      {(!hideEmptyParamsTable || paramItems.length > 0) && (
        <>
          <EuiAccordion
            id="inputs_parameters"
            initialIsOpen={isAlwaysExpanded}
            isDisabled={isAlwaysExpanded}
            arrowDisplay={isAlwaysExpanded ? "none" : undefined}
            buttonContent={
              <EuiText
                color="rgb(26,28,33)"
                size="xs"
                style={{ cursor: "pointer" }}
              >
                <b>{parametersLabel}</b>
              </EuiText>
            }
          >
            <EuiFormRow fullWidth>
              <EuiBasicTable
                items={paramItems}
                columns={
                  hideTableSelector
                    ? columns.filter((c) => c.name !== "Source")
                    : columns
                }
              />
            </EuiFormRow>
          </EuiAccordion>
          <EuiSpacer />
        </>
      )}
    </>
  );
};
